import { useState } from 'react';
import { Search, Check, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

interface NextStepsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (options: { research: boolean; email: boolean }) => void;
  onSkip: () => void;
}

export const NextStepsDialog = ({
  open,
  onOpenChange,
  onGenerate,
  onSkip
}: NextStepsDialogProps) => {
  const [generateResearch, setGenerateResearch] = useState(true);
  const [generateEmail, setGenerateEmail] = useState(false);

  const handleGenerate = () => {
    onGenerate({ research: generateResearch, email: generateEmail });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">Next Step: Prepare Materials</DialogTitle>
          <DialogDescription className="text-center">
            Prepare company research or an RFI email. Company research can take up to 30 minutes.
          </DialogDescription>
        </DialogHeader>

        {/* Options */}
        <div className="space-y-3 py-4">
          <div
            onClick={() => setGenerateResearch(!generateResearch)}
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              generateResearch 
                ? 'border-foreground/20 bg-muted/30' 
                : 'border-border/50 hover:border-border'
            }`}
          >
            <Checkbox 
              checked={generateResearch} 
              onCheckedChange={() => setGenerateResearch(!generateResearch)}
              className="pointer-events-none"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Target Company Research</p>
              <p className="text-xs text-muted-foreground">Industry trends, competitors, recent news</p>
            </div>
            <Search className="w-4 h-4 text-muted-foreground" />
          </div>

          <div
            onClick={() => setGenerateEmail(!generateEmail)}
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              generateEmail 
                ? 'border-foreground/20 bg-muted/30' 
                : 'border-border/50 hover:border-border'
            }`}
          >
            <Checkbox 
              checked={generateEmail} 
              onCheckedChange={() => setGenerateEmail(!generateEmail)}
              className="pointer-events-none"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Send RFI Email</p>
              <p className="text-xs text-muted-foreground">Generate and send RFI request email</p>
            </div>
            <Mail className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>

        <DialogFooter className="sm:justify-between gap-2">
          <Button variant="ghost" onClick={onSkip} className="text-muted-foreground">
            Skip for now
          </Button>
          <Button onClick={handleGenerate} className="gap-2">
            {generateResearch || generateEmail ? 'Generate' : 'Continue'}
            <Check className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
